/**
 * Formatting + constants for the "Analyze Value Drivers" flow (INS-11749) — the
 * eval-3-vs-eval-3 NPV comparison launched from the Package Comparison result page.
 *
 * The analysis endpoint returns NPV in raw dollars; the UI shows it in $M. Counts
 * render via <FormattedCount/>, so only the $M and percent helpers live here.
 */
import routes from '~/routes';
import { format0, format1, format2 } from 'utils/number';
import { fromDateParam } from 'utils/dates';
import {
  SLOT_CATEGORY_KEYS,
  SLOT_CATEGORY_LABELS,
} from 'utils/slot-categories';
import { STATUS_DISPLAY_MAP } from 'pages/crm-tracts-subtracts/utils';
import type { StatusItem } from 'pages/crm-tracts-subtracts/types';
import type {
  ChartExportConfig,
  RescatOption,
  RunProgressHeadline,
  RunProgressStepResponse,
  RunStatus,
  SlotRowsPageResponse,
  SlotSummaryRow,
  SummaryRow,
  SummaryRowOptions,
  WaterfallBar,
  WaterfallColumnExtent,
  WaterfallProjection,
} from './types';

const EM_DASH = '—';
const MINUS = '−'; // U+2212, to match the app's numeric minus

/**
 * Whether a value reads as zero at one decimal — so a change too small to show (float noise on an
 * unchanged slot) is formatted unsigned and toned neutral, like a true zero. Tested numerically
 * (one decimal rounds anything under 0.05 to 0.0) rather than by formatting the value again: every
 * table row asks, and each format builds an Intl formatter.
 *
 * @param {number} value - The value, in the unit it's displayed in ($M or percent).
 * @return {boolean} True when it displays as 0.0.
 */
function readsAsZero(value: number): boolean {
  return Math.abs(value) < 0.05;
}

/**
 * The sign prefix a one-decimal value displays with.
 *
 * @param {number} value - The value, in the unit it's displayed in ($M or percent).
 * @return {string} `+`, `−`, or '' when the value reads as zero.
 */
function displayedSign(value: number): string {
  if (readsAsZero(value)) return '';
  return value > 0 ? '+' : MINUS;
}

/**
 * Formats a dollar amount as $M to one decimal, a negative with the page's minus before the "$".
 *
 * @param {number | null | undefined} dollars - The amount; null/undefined = unpriced.
 * @return {string} e.g. `$451.2M` / `−$3.2M` (one that reads as $0.0M is unsigned), or an em-dash
 *   for a null value.
 */
export function formatMillions(dollars: number | null | undefined): string {
  if (dollars === null || dollars === undefined) return EM_DASH;
  const millions = dollars / 1_000_000;
  const sign = millions < 0 && !readsAsZero(millions) ? MINUS : '';
  return `${sign}$${format1(Math.abs(millions))}M`;
}

/**
 * The units the Summary Table shows dollars in, largest first. An amount takes the largest unit it
 * reaches once rounded — $999,950 reads $1.0M, not $1,000.0K — so each reads in its own magnitude:
 * $8.2M, $320.5K, $450, and under a dollar in cents, $0.30.
 */
const DOLLAR_UNITS: {
  from: number;
  size: number;
  suffix: string;
  format: (value: number) => string;
}[] = [
  { from: 999_950_000, size: 1_000_000_000, suffix: 'B', format: format1 },
  { from: 999_950, size: 1_000_000, suffix: 'M', format: format1 },
  { from: 999.5, size: 1_000, suffix: 'K', format: format1 },
  { from: 0.995, size: 1, suffix: '', format: format0 },
  { from: 0, size: 1, suffix: '', format: format2 },
];

/**
 * Whether a dollar amount reads as $0 — under half a cent, which cents round away: the float noise
 * on an unchanged slot. The server draws the same line for a slot that didn't change
 * (`SlotContribution::UNCHANGED_BELOW`), so a slot the page lists never reads $0.
 *
 * @param {number} dollars - The amount.
 * @return {boolean} True when it displays as $0.
 */
function dollarsReadAsZero(dollars: number): boolean {
  return Math.abs(dollars) < 0.005;
}

/**
 * A dollar amount's size in its own unit (see {@link DOLLAR_UNITS}), without a sign.
 *
 * @param {number} dollars - The amount.
 * @return {string} e.g. `8.2M`, `320.5K`, `450`, `0.30`; `0` for one that reads as $0.
 */
function dollarMagnitude(dollars: number): string {
  if (dollarsReadAsZero(dollars)) return '0';
  const abs = Math.abs(dollars);
  const unit =
    DOLLAR_UNITS.find((candidate) => abs >= candidate.from) ??
    DOLLAR_UNITS[DOLLAR_UNITS.length - 1];
  return `${unit.format(abs / unit.size)}${unit.suffix}`;
}

/**
 * Formats a dollar amount in the unit that fits its size (see {@link DOLLAR_UNITS}), a negative
 * with the page's minus before the "$".
 *
 * @param {number | null | undefined} dollars - The amount; null/undefined = unpriced.
 * @return {string} e.g. `$8.2M` / `$320.5K` / `$450` / `−$3.2M`, or an em-dash for a null value.
 */
export function formatDollars(dollars: number | null | undefined): string {
  if (dollars === null || dollars === undefined) return EM_DASH;
  const sign = dollars < 0 && !dollarsReadAsZero(dollars) ? MINUS : '';
  return `${sign}$${dollarMagnitude(dollars)}`;
}

/**
 * Formats a dollar change, signed, in the unit that fits its size; one that reads as $0 (float
 * noise on an unchanged slot) is unsigned.
 *
 * @param {number | null | undefined} dollars - The change; null/undefined = unpriced.
 * @return {string} e.g. `+$8.1M` / `−$120.0K` / `$0`, or an em-dash for a null value.
 */
export function formatSignedDollars(
  dollars: number | null | undefined
): string {
  if (dollars === null || dollars === undefined) return EM_DASH;
  let sign = '';
  if (!dollarsReadAsZero(dollars)) sign = dollars > 0 ? '+' : MINUS;
  return `${sign}$${dollarMagnitude(dollars)}`;
}

/**
 * Formats a dollar delta as a signed $M value. A delta that reads as $0.0M is unsigned.
 *
 * @param {number | null | undefined} dollars - The delta; null/undefined = unpriced.
 * @return {string} e.g. `+$8.1M` / `−$64.8M` / `$0.0M`, or an em-dash for a null value.
 */
export function formatSignedMillions(
  dollars: number | null | undefined
): string {
  if (dollars === null || dollars === undefined) return EM_DASH;
  const millions = dollars / 1_000_000;
  return `${displayedSign(millions)}$${format1(Math.abs(millions))}M`;
}

/**
 * Formats a waterfall bar's change: in $M ({@link formatSignedMillions}), unless it would read as
 * $0.0M there — a change under $50K — when it reads in its own unit, as the Summary Table shows it
 * ({@link formatSignedDollars}). So a small bar's label matches its height, and only a true $0
 * reads `$0`. Toned by {@link dollarsDeltaSign} to match.
 *
 * @param {number} dollars - The bar's change.
 * @return {string} e.g. `+$8.1M`, `−$12.0K`, `+$450`, `$0`.
 */
export function formatBarChange(dollars: number): string {
  return readsAsZero(dollars / 1_000_000)
    ? formatSignedDollars(dollars)
    : formatSignedMillions(dollars);
}

/**
 * A per-unit price to two decimals, e.g. `$72.30`; em-dash for null.
 *
 * @param {number | null | undefined} value - The price ($/bbl or $/mcf).
 * @return {string} The formatted price, or an em-dash for a null value.
 */
export function formatPrice(value: number | null | undefined): string {
  if (value === null || value === undefined) return EM_DASH;
  return `$${format2(value)}`;
}

/**
 * A signed per-unit price delta to two decimals, e.g. `+$2.30` / `−$0.14`; em-dash for
 * null (no realized volume on a side).
 *
 * @param {number | null | undefined} value - The price delta ($/bbl or $/mcf).
 * @return {string} The signed price, or an em-dash for a null value.
 */
export function formatSignedPrice(value: number | null | undefined): string {
  if (value === null || value === undefined) return EM_DASH;
  const sign = value > 0 ? '+' : value < 0 ? MINUS : '';
  return `${sign}$${format2(Math.abs(value))}`;
}

/**
 * Formats a server `YYYY-MM-DD` effective date as a `MM/DD/YYYY` display string.
 *
 * @param {string | null | undefined} value - The server date string.
 * @return {string} The `MM/DD/YYYY` date, or an em-dash for a null/invalid value.
 */
export function formatEffectiveDate(value: string | null | undefined): string {
  return fromDateParam(value) || EM_DASH;
}

/**
 * Formats a percent as a signed value to one decimal. A percent that reads as 0.0% is unsigned.
 *
 * @param {number | null | undefined} pct - The percent; null/undefined = unavailable.
 * @return {string} e.g. `+2.4%` / `−14.4%` / `0.0%`, or an em-dash for a null value.
 */
export function formatPercent(pct: number | null | undefined): string {
  if (pct === null || pct === undefined) return EM_DASH;
  return `${displayedSign(pct)}${format1(Math.abs(pct))}%`;
}

/**
 * Classifies a delta for positive/negative color styling.
 *
 * @param {number | null | undefined} value - The delta value.
 * @return {'positive' | 'negative' | 'neutral'} The tone; null/zero → neutral.
 */
export function deltaSign(
  value: number | null | undefined
): 'positive' | 'negative' | 'neutral' {
  if (value === null || value === undefined || value === 0) return 'neutral';
  return value > 0 ? 'positive' : 'negative';
}

/**
 * Classifies a dollar change for color styling as {@link formatSignedDollars} displays it: one
 * that reads as $0 is neutral, matching its unsigned text.
 *
 * @param {number | null | undefined} dollars - The change (dollars).
 * @return {'positive' | 'negative' | 'neutral'} The tone.
 */
export function dollarsDeltaSign(
  dollars: number | null | undefined
): 'positive' | 'negative' | 'neutral' {
  if (dollars === null || dollars === undefined) return 'neutral';
  return dollarsReadAsZero(dollars) ? 'neutral' : deltaSign(dollars);
}

/**
 * Classifies a dollar delta for color styling as it's displayed in $M: one that reads as $0.0M
 * is neutral, matching its unsigned text.
 *
 * @param {number | null | undefined} dollars - The delta (dollars).
 * @return {'positive' | 'negative' | 'neutral'} The tone.
 */
export function millionsDeltaSign(
  dollars: number | null | undefined
): 'positive' | 'negative' | 'neutral' {
  if (dollars === null || dollars === undefined) return 'neutral';
  return readsAsZero(dollars / 1_000_000) ? 'neutral' : deltaSign(dollars);
}

/**
 * Classifies a percent change for color styling as it's displayed: one that reads as 0.0% is
 * neutral, matching its unsigned text.
 *
 * @param {number | null | undefined} pct - The percent change.
 * @return {'positive' | 'negative' | 'neutral'} The tone.
 */
export function percentDeltaSign(
  pct: number | null | undefined
): 'positive' | 'negative' | 'neutral' {
  if (pct === null || pct === undefined) return 'neutral';
  return readsAsZero(pct) ? 'neutral' : deltaSign(pct);
}

/**
 * Walks the waterfall bars onto their running cumulative, once. Each absolute anchor is
 * grounded at zero and resets the cursor to its value; each delta floats where the
 * previous bar left off. The chart columns (`buildWaterfallColumns`, scaled to $M) and the
 * Summary Table's group/factor rows (`toSummaryRows`) both derive from this one walk.
 * Pure — safe to unit-test.
 *
 * @param {WaterfallBar[]} bars - The waterfall bars in display order (raw dollars).
 * @return {WaterfallProjection[]} Each bar with its origin/target brackets (dollars).
 */
export function projectWaterfall(bars: WaterfallBar[]): WaterfallProjection[] {
  let cursor = 0;
  return bars.map((bar) => {
    const origin = bar.type === 'absolute' ? 0 : cursor;
    const target = bar.type === 'absolute' ? bar.value : cursor + bar.value;
    cursor = target;
    return {
      key: bar.key,
      label: bar.label,
      type: bar.type,
      origin,
      target,
      value: bar.value,
    };
  });
}

/**
 * Percent change of a delta against its base's magnitude, so the sign follows the delta (a
 * negative Origin that improves reads as a gain); null when the base is zero (avoids a
 * divide-by-zero — the table shows "—" and the header leaves the percent out). The one rule for
 * the Results header and every Summary Table row (the Step-2 tiles' BE percent divides the same
 * way).
 *
 * @param {number} delta - The change (dollars).
 * @param {number} base - The origin the change is measured against (dollars).
 * @return {number | null} The signed percent, or null when base is 0.
 */
export function percentChange(delta: number, base: number): number | null {
  if (!base) return null;
  return (delta / Math.abs(base)) * 100;
}

/**
 * Derives the Step-4 Summary Table rows from a level's waterfall bars — one row per delta
 * bar, carrying the running-cumulative brackets as its Origin/Target NPV ({@link projectWaterfall},
 * the same walk the chart draws), plus (at the overview) a bolded Total row spanning the two
 * absolute anchors. A nested level is shifted by `originBase` so it sits inside its parent row.
 *
 * @param {WaterfallBar[]} bars - The level's bars (the overview, or a drill level).
 * @param {string[]} [drillableKeys] - Keys of the bars drillable at this level.
 * @param {SummaryRowOptions} [options] - Total row and nesting base.
 * @return {SummaryRow[]} The rows in display order.
 */
export function toSummaryRows(
  bars: WaterfallBar[],
  drillableKeys: string[] = [],
  { includeTotal = true, originBase }: SummaryRowOptions = {}
): SummaryRow[] {
  const projection = projectWaterfall(bars);
  const anchor = projection[0]?.type === 'absolute' ? projection[0].value : 0;
  const offset = originBase === undefined ? 0 : originBase - anchor;

  const rows: SummaryRow[] = projection
    .filter((step) => step.type === 'delta')
    .map((step) => {
      const origin = step.origin + offset;
      return {
        key: step.key,
        label: step.label,
        origin,
        target: step.target + offset,
        delta: step.value,
        deltaPct: percentChange(step.value, origin),
        drillable: drillableKeys.includes(step.key),
        isTotal: false,
      };
    });

  if (includeTotal) {
    const first = projection[0];
    const last = projection[projection.length - 1];
    if (first?.type === 'absolute' && last?.type === 'absolute') {
      const delta = last.value - first.value;
      rows.push({
        key: 'total',
        label: 'Total',
        origin: first.value,
        target: last.value,
        delta,
        deltaPct: percentChange(delta, first.value),
        drillable: false,
        isTotal: true,
      });
    }
  }
  return rows;
}

/**
 * Each loaded page's slot rows, built once per page. React Query keeps a loaded page's object as
 * the list grows, so its rows keep their identity and the table re-renders only a new page's rows.
 */
const slotRowsByPage = new WeakMap<SlotRowsPageResponse, SlotSummaryRow[]>();

/**
 * One page's slot rows — each slot's own NPV before → after the step, the change, and the change
 * as a percent of its before value (null when there is none — an added slot — or it's $0).
 *
 * @param {SlotRowsPageResponse} page - The page.
 * @return {SlotSummaryRow[]} Its rows, the same array for the same page.
 */
function pageSlotRows(page: SlotRowsPageResponse): SlotSummaryRow[] {
  const cached = slotRowsByPage.get(page);
  if (cached) return cached;
  const rows = page.results.map((slot) => ({
    key: slot.key,
    label: slot.label,
    origin: slot.originValue,
    target: slot.targetValue,
    delta: slot.delta,
    deltaPct:
      slot.originValue === null
        ? null
        : percentChange(slot.delta, slot.originValue),
    drillable: false,
    isTotal: false,
  }));
  slotRowsByPage.set(page, rows);
  return rows;
}

/**
 * The Summary Table's slot rows from the pages of a factor's slot list loaded so far (see
 * pageSlotRows). A slot is listed once, even if the list shifted between two page reads (an eval
 * recalculated mid-scroll).
 *
 * @param {SlotRowsPageResponse[]} pages - The pages loaded so far, in order.
 * @return {SlotSummaryRow[]} The rows, in list order.
 */
export function toSlotRows(pages: SlotRowsPageResponse[]): SlotSummaryRow[] {
  const seen = new Set<string>();
  return pages.flatMap(pageSlotRows).filter((row) => {
    if (seen.has(row.key)) return false;
    seen.add(row.key);
    return true;
  });
}

/** Longest a slot bar's x-axis label runs before it's shortened (the tooltip shows it whole). */
export const SLOT_AXIS_LABEL_MAX_LENGTH = 24;

/**
 * A slot bar's x-axis label, shortened in the middle when longer than
 * {@link SLOT_AXIS_LABEL_MAX_LENGTH} so the part that tells two slots apart survives: a well's
 * " (UWI)" suffix is kept whole ("GOLIATH 24… (3002555540)"); any other label keeps its start and
 * its end.
 *
 * @param {string} label - The slot's full label.
 * @return {string} The label as the axis draws it.
 */
export function slotAxisLabel(label: string): string {
  if (label.length <= SLOT_AXIS_LABEL_MAX_LENGTH) return label;
  const room = SLOT_AXIS_LABEL_MAX_LENGTH - 1; // less the ellipsis
  const uwi = / \([^()]+\)$/.exec(label)?.[0];
  const tail =
    uwi && uwi.length < room ? uwi : label.slice(-Math.floor(room / 2));
  return `${label.slice(0, room - tail.length).trimEnd()}…${tail}`;
}

/**
 * The waterfall's x-axis label for a bar: a slot bar (key `slot_<id>`) shortened to fit a crowded
 * slot drill-down (see {@link slotAxisLabel}); every other bar — the anchors, groups and factors —
 * in full.
 *
 * @param {string} key - The bar's key.
 * @param {string} label - The bar's full label.
 * @return {string} The label as the axis draws it.
 */
export function waterfallAxisLabel(key: string, label: string): string {
  return isSlotKey(key) ? slotAxisLabel(label) : label;
}

/**
 * Whether a bar key names a slot (`slot_<id>`). Factor, group and anchor keys never start that
 * way — the overview's `slots_removed` / `slots_added` included.
 *
 * @param {string} key - The bar's key.
 * @return {boolean} True for a slot's bar.
 */
export function isSlotKey(key: string): boolean {
  return key.startsWith('slot_');
}

/**
 * The waterfall's value-axis (y) label for a tick, in the page's own $M format (see
 * {@link formatMillions}) — so ticks read like the bars' labels, down to a slot drill-down's
 * fractions of a million.
 *
 * @param {number} millions - The tick's value, in $M.
 * @return {string} e.g. `$1,250.0M`, `$0.4M`, `−$50.0M`.
 */
export function waterfallValueAxisLabel(millions: number): string {
  return formatMillions(millions * 1_000_000);
}

/** React Query key root for the comparison analysis (keyed by the eval-3 pair). */
export const ANALYSIS_QUERY_KEY = 'compare-analyze:analysis';

/** React Query key root for a comparison run (keyed by the run id). */
export const RUN_QUERY_KEY = 'compare-analyze:run';

/** React Query key root for a group's factor-level drill-down waterfall. */
export const GROUP_FACTORS_QUERY_KEY = 'compare-analyze:group-factors';

/** React Query key root for a factor's per-slot drill-down waterfall. */
export const SLOT_WATERFALL_QUERY_KEY = 'compare-analyze:slots';

/**
 * React Query key root for a factor's paged slot list: the Summary Table's, biggest change first,
 * and a drill-down chart's, highest to lowest.
 */
export const SLOT_ROWS_QUERY_KEY = 'compare-analyze:slot-rows';

/** Pusher event a run pushes each time it advances; the FE refetches on it. */
export const RUN_UPDATED_EVENT = 'run_updated';

/**
 * Pusher's own event once a channel subscription is live. Pusher doesn't replay, so the FE
 * reconciles on it: anything pushed before the subscription registered would otherwise be lost.
 */
export const PUSHER_SUBSCRIBED_EVENT = 'pusher:subscription_succeeded';

/** Statuses in which a run has stopped advancing (no further Pusher events). */
export const TERMINAL_RUN_STATUSES: RunStatus[] = [
  'complete',
  'failed',
  'non_viable',
];

/**
 * Whether a run is still being calculated (pending or running) — the one test behind following
 * it live and holding its chart and table as skeletons.
 *
 * @param {RunStatus} status - The run's status.
 * @return {boolean} True until the run reaches a terminal status.
 */
export function isRunLive(status: RunStatus): boolean {
  return !TERMINAL_RUN_STATUSES.includes(status);
}

/**
 * The REORDERABLE factor groups shown in Step 3, in their default engine order. Only the
 * groups the engine attributes end to end appear (Pricing → price deck + differentials,
 * Costs → tax, Discount Rates → discount rate + hurdle, Ownership & Risk → risk + NRI,
 * Timing → effective date + development pace, Production → volumes). Keep in lockstep with
 * `FactorDiff`'s group set: a factor whose group is absent here would be dropped from the
 * Step-3 rows (see `useFactorOrder`).
 *
 * The two slot-population factors (Slots Removed / Slots Added, group `slotCoverage`) are
 * NOT here — they are fixed at the ends of the Step-4 waterfall and never reordered.
 */
export const FACTOR_GROUPS: { key: string; label: string }[] = [
  { key: 'pricing', label: 'Pricing' },
  { key: 'costs', label: 'Costs' },
  { key: 'discountRates', label: 'Discount Rates' },
  { key: 'ownershipRisk', label: 'Ownership & Risk' },
  { key: 'timing', label: 'Timing' },
  { key: 'production', label: 'Production' },
];

/** Default factor-group order (group keys) before the user reorders. */
export const DEFAULT_FACTOR_GROUP_ORDER = FACTOR_GROUPS.map((g) => g.key);

/**
 * Reconciles a (possibly stale) factor-group order against FACTOR_GROUPS: keeps the
 * user's sequence but drops unknown/duplicate keys and appends any missing group, so
 * the result is always every group key exactly once. This keeps a stale order (e.g. one
 * carried across pairs, or predating a newly-added group) from dropping or duplicating
 * a row.
 *
 * @param {string[] | null} order - The current order (null = use the default).
 * @return {string[]} Every FACTOR_GROUPS key exactly once, in the best-effort order.
 */
export function reconcileFactorGroupOrder(order: string[] | null): string[] {
  const known = new Set(DEFAULT_FACTOR_GROUP_ORDER);
  const kept = [
    ...new Set(
      (order ?? DEFAULT_FACTOR_GROUP_ORDER).filter((key) => known.has(key))
    ),
  ];
  const missing = DEFAULT_FACTOR_GROUP_ORDER.filter(
    (key) => !kept.includes(key)
  );
  return [...kept, ...missing];
}

/** How many matched-factor chips to show before collapsing the rest into "+N more". */
export const MATCHED_FACTOR_CHIP_LIMIT = 12;

/**
 * The Results page's reserve-category filter options: the calculator's slot_category values,
 * from the shared category list; selecting one scopes every level of the results (the
 * overview, the drill-downs, the Summary Table and the header) to that category's slots,
 * computed server-side.
 */
export const RESCAT_OPTIONS: RescatOption[] = [
  { key: null, label: 'All Rescats' },
  ...SLOT_CATEGORY_KEYS.map((key) => ({
    key,
    label: SLOT_CATEGORY_LABELS[key],
  })),
];

/**
 * The filter option for a reserve category (falls back to "All Rescats").
 *
 * @param {string | null} rescat - The selected category key, or null for all.
 * @return {RescatOption} The matching option.
 */
export function rescatOption(rescat: string | null): RescatOption {
  return (
    RESCAT_OPTIONS.find((option) => option.key === rescat) ?? RESCAT_OPTIONS[0]
  );
}

/**
 * The query string for a run or drill-down request: its reserve category and, for a slot level
 * whose $0 slots the page shows, `include_unchanged=true` — the server otherwise leaves out the
 * slots that didn't change. '' when neither applies, so an unscoped URL is unchanged.
 *
 * @param {string | null} rescat - The reserve category, or null for all.
 * @param {boolean} includeUnchanged - Keep the $0 slots.
 * @return {string} The query string.
 */
export function drillQuery(
  rescat: string | null,
  includeUnchanged: boolean
): string {
  const params = new URLSearchParams();
  if (rescat) params.set('rescat', rescat);
  if (includeUnchanged) params.set('include_unchanged', 'true');
  const query = params.toString();
  return query ? `?${query}` : '';
}

/**
 * The " · PDP" suffix a title carries when the results are scoped to a category.
 *
 * @param {string | null} rescat - The category, or null for all.
 * @return {string} The suffix ('' when unscoped).
 */
export function rescatScopeSuffix(rescat: string | null): string {
  return rescat ? ` · ${rescatOption(rescat).label}` : '';
}

/**
 * A chart's image-export config scoped to the reserve category it shows: the title carries the
 * " · PDP" suffix and the filename the category, so each category's export is told apart.
 *
 * @param {string} label - The export title, unscoped.
 * @param {string} filenamePrefix - The filename prefix, unscoped.
 * @param {string | null} rescat - The shown reserve category, or null for all.
 * @return {ChartExportConfig} The scoped export config.
 */
export function rescatExportConfig(
  label: string,
  filenamePrefix: string,
  rescat: string | null
): ChartExportConfig {
  return {
    label: `${label}${rescatScopeSuffix(rescat)}`,
    filenamePrefix: rescat ? `${filenamePrefix}-${rescat}` : filenamePrefix,
  };
}

/**
 * The message shown when a category has nothing to show. Worded neutrally: an empty scoped
 * waterfall can also mean an unpriced anchor.
 *
 * @param {string} rescat - The category.
 * @return {string} The message.
 */
export function rescatEmptyMessage(rescat: string): string {
  return `Nothing to show for ${
    rescatOption(rescat).label
  } in this comparison.`;
}

/**
 * A run's stages as `StatusChecklist` rows: the display word from the app's shared
 * `STATUS_DISPLAY_MAP` ("In Progress", "Completed", …), the raw status for the row's icon, and
 * the failure / not-run reason as the row's detail tooltip.
 *
 * @param {RunProgressStepResponse[]} progress - The run's stages, in run order.
 * @return {StatusItem[]} One checklist row per stage.
 */
export function toStatusItems(
  progress: RunProgressStepResponse[]
): StatusItem[] {
  return progress.map((stage) => ({
    name: stage.name,
    label: stage.label,
    value: STATUS_DISPLAY_MAP[stage.status] ?? stage.status,
    status: stage.status,
    details: stage.details ?? undefined,
  }));
}

/**
 * The headline over a stopped run's stages (the panel under "Comparison failed"): the stage it
 * stopped at, and how far it got — completed stages over all stages, rounded, since it feeds the
 * progress bar's `aria-valuenow`.
 *
 * @param {RunProgressStepResponse[]} progress - The run's stages, in run order.
 * @return {RunProgressHeadline} The status label and percent.
 */
export function runProgressHeadline(
  progress: RunProgressStepResponse[]
): RunProgressHeadline {
  const failed = progress.find((stage) => stage.status === 'failed');
  const done = progress.filter((stage) => stage.status === 'processed').length;
  return {
    statusLabel: failed ? `Stopped at ${failed.label}` : 'Comparison stages',
    percent:
      progress.length > 0 ? Math.round((done / progress.length) * 100) : 0,
  };
}

/**
 * Client timeout (ms) for the analysis request. The `api` ky client defaults to
 * 30s, but the analysis is warehouse-heavy on a cold run (two Crunchy foreign tables +
 * two parquet scans + slot-pricing blob reads), so it gets a longer per-request
 * ceiling. Set to 30 min to accommodate slow runs — notably a dev app pointed at the
 * remote Crunchy DB, where every slot-allocations row is pulled back over the network.
 * This is a stopgap: if real-world runs routinely need this long, move the engine to
 * async (Sidekiq + Pusher) per the plan's ADR-4.
 */
export const ANALYSIS_TIMEOUT_MS = 1_800_000;

/** React Query mutation key of Export by property (see `useIsExportingByProperty`). */
export const PROPERTY_EXPORT_MUTATION_KEY = 'compare-analyze:property-export';

/**
 * Export by property's wait (see `armJobCompletion`): a read every 5s, as a backstop for Pusher,
 * and how long it waits before giving up — timed from where the export is, since the job's own
 * 600s ceiling (`AzureExportJob::EXPORT_TIMEOUT_SECONDS`) starts only once it runs:
 * - `queuedMs`, from when the server took the export (its POST answered), while the job waits its
 *   turn (the within-five-minutes queue can back up);
 * - `runningMs`, from the first read that finds it running: just past that ceiling, by when the
 *   server has failed the download.
 */
export const PROPERTY_EXPORT_POLL = {
  delayMs: 5_000,
  intervalMs: 5_000,
  queuedMs: 1_800_000,
  runningMs: 660_000,
};

/**
 * Builds a URL for a step of the Analyze-Value-Drivers flow, carrying the eval-3
 * pair as query params. The pair is the flow's single source of truth for the
 * Origin/Target ids (mirrors how the compare result page is URL-driven and
 * deep-linkable), so every step's link threads the same two ids forward.
 *
 * @param {string} path - The target route path (a `routes.compareAnalyze*Path`).
 * @param {number} originEvalId - The Origin EvaluationCalculation id (side A).
 * @param {number} targetEvalId - The Target EvaluationCalculation id (side B).
 * @return {string} The path with `side_a_evaluation_id` / `side_b_evaluation_id`.
 */
export function analyzeHref(
  path: string,
  originEvalId: number,
  targetEvalId: number
): string {
  const params = new URLSearchParams({
    side_a_evaluation_id: String(originEvalId),
    side_b_evaluation_id: String(targetEvalId),
  });
  return `${path}?${params.toString()}`;
}

/**
 * Appends a `run_id` to an existing analyze href (from {@link analyzeHref}) so the
 * Results step deep-links to a specific run — the run id is the flow's source of
 * truth for which comparison to render.
 *
 * @param {string} href - An analyze href already carrying the eval-3 pair.
 * @param {number} runId - The comparison run id.
 * @return {string} The href with `run_id` appended.
 */
export function withRunId(href: string, runId: number): string {
  const separator = href.includes('?') ? '&' : '?';
  return `${href}${separator}run_id=${runId}`;
}

/**
 * The Package Comparison **result** URL for an eval-3 pair — the breadcrumb root, so
 * "Compare Evaluations" returns the user to the result they launched the analysis from
 * (both sides are eval-3), not the picker. Mirrors `wizard/utils.ts buildCompareUrl`.
 *
 * @param {number} originEvalId - The Origin EvaluationCalculation id (side A).
 * @param {number} targetEvalId - The Target EvaluationCalculation id (side B).
 * @return {string} The compare-result path with the eval-3 pair + versions.
 */
export function compareResultHref(
  originEvalId: number,
  targetEvalId: number
): string {
  const params = new URLSearchParams({
    side_a_eval_version: 'eval_3',
    side_a_evaluation_id: String(originEvalId),
    side_b_eval_version: 'eval_3',
    side_b_evaluation_id: String(targetEvalId),
    variance_threshold: '0.05',
  });
  return `${routes.compareResultPath}?${params.toString()}`;
}

/** A waterfall's value-axis room below its lowest bar, as a share of the bars' span. */
export const VALUE_AXIS_PAD_BELOW = 0.06;

/** A waterfall's value-axis room above its highest bar (its value labels sit there), as a share of the span. */
export const VALUE_AXIS_PAD_ABOVE = 0.16;

/**
 * The least span ($M) a zoomed value axis fits to: enough for its one-decimal $M ticks to differ, so
 * a few-$K bar never fills the plot under ticks that all read the same.
 */
const MIN_FITTED_VALUE_SPAN_M = 0.5;

/**
 * The value (y) range a zoomable waterfall fits to the bars in an x range: each delta bar's extent
 * and each anchor's top — not an anchor's zero base, whose height would flatten a window of small
 * slot changes into slivers (the anchors run off the bottom instead). A bar counts while any of its
 * column (its x ± half a column) is in view, so one cut by an edge is never cut short. Falls back
 * to every bar when none is in view. Bars in view that span less than
 * {@link MIN_FITTED_VALUE_SPAN_M} are centred in that span instead, so their size stays honest
 * against the axis — unless that would take bars all on one side of $0 past it, when the span
 * runs from $0. Pure.
 *
 * @param {WaterfallColumnExtent[]} columns - The chart's columns, in x order.
 * @param {number} xMin - The view's left edge, in x data units.
 * @param {number} xMax - The view's right edge, in x data units.
 * @return {object} The y range (`min`, `max`, $M), padded like the full chart's.
 */
export function visibleValueRange(
  columns: WaterfallColumnExtent[],
  xMin: number,
  xMax: number
): { min: number; max: number } {
  const inView = columns.filter(
    (_, index) => index + 0.5 > xMin && index - 0.5 < xMax
  );
  const values = (inView.length > 0 ? inView : columns).flatMap((column) => {
    if (column.tone === 'absolute') return [column.runningM];
    return [column.loM, column.hiM];
  });
  const low = Math.min(...values);
  const high = Math.max(...values);
  const widen = Math.max(MIN_FITTED_VALUE_SPAN_M - (high - low), 0) / 2;
  // Widened evenly, except that bars all on one side of $0 never gain an axis past it.
  let lo = low - widen;
  let hi = high + widen;
  if (low >= 0 && lo < 0) [lo, hi] = [0, hi - lo];
  else if (high <= 0 && hi > 0) [lo, hi] = [lo - hi, 0];
  const span = hi - lo;
  return {
    min: lo - span * VALUE_AXIS_PAD_BELOW,
    max: hi + span * VALUE_AXIS_PAD_ABOVE,
  };
}
